/**
 * DS Coder — AI-assisted pack authoring.
 *
 * Asks the model to draft/expand a knowledge pack (coding standards, common
 * errors, golden libraries) as strict JSON, then validates it with zod before
 * returning. The result is a *draft* for the user to review and save — the AI
 * never writes to the store directly.
 */
import { z } from "zod";
import { aiService } from "@/services/aiService";
import type { DsCoderPackInput } from "@/types/dsCoderPacks";

const DraftSchema = z.object({
  label: z.string().trim().min(1).optional(),
  docsLibraryId: z.string().trim().nullable().optional(),
  standards: z.array(z.string().trim().min(1)).default([]),
  commonErrors: z
    .array(z.object({ error: z.string().trim().min(1), fix: z.string().trim().min(1) }))
    .default([]),
  goldenLibraries: z
    .array(
      z.object({
        need: z.string().trim().min(1),
        use: z.string().trim().min(1),
        avoid: z.string().trim().optional(),
      })
    )
    .default([]),
});

export type PackDraft = Pick<
  DsCoderPackInput,
  "standards" | "commonErrors" | "goldenLibraries"
> & { label?: string; docsLibraryId?: string | null };

function extractJson(text: string): string {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidate = (fenced ? fenced[1] : text).trim();
  const start = candidate.indexOf("{");
  const end = candidate.lastIndexOf("}");
  if (start !== -1 && end !== -1 && end > start) return candidate.slice(start, end + 1);
  return candidate;
}

function buildPrompt(params: {
  techId: string;
  label: string;
  existing?: PackDraft;
  instruction?: string;
}): string {
  const { techId, label, existing, instruction } = params;
  const existingBlock = existing
    ? `\nHere is the EXISTING pack — improve and expand it (keep good entries, remove weak/duplicate ones):\n${JSON.stringify(
        {
          standards: existing.standards,
          commonErrors: existing.commonErrors,
          goldenLibraries: existing.goldenLibraries,
        },
        null,
        2
      )}\n`
    : "";
  const extra = instruction?.trim() ? `\nAdditional guidance from the user: ${instruction.trim()}\n` : "";

  return `You are curating a coding-standards "knowledge pack" for the technology "${label}" (id: ${techId}) used by an AI coding assistant.

Produce a JSON object with EXACTLY these keys:
- "standards": string[] — concise, actionable coding standards / architectural rules (6-10 items).
- "commonErrors": { "error": string, "fix": string }[] — frequent mistakes and how to avoid them (3-6 items).
- "goldenLibraries": { "need": string, "use": string, "avoid"?: string }[] — recommended packages per need (2-6 items).
- "docsLibraryId"?: string | null — a Context7 library id like "owner/project" if you are confident, else null.

Rules:
- Each string must be specific and practical, not generic filler.
- Prefer modern, current best practices.
- Output ONLY the JSON object. No prose, no markdown fences.${existingBlock}${extra}`;
}

export async function generatePackDraft(params: {
  techId: string;
  label: string;
  existing?: PackDraft;
  instruction?: string;
}): Promise<PackDraft> {
  const prompt = buildPrompt(params);
  const raw = await aiService.complete(prompt, {
    model: "openai/gpt-4o",
    maxTokens: 1500,
    temperature: 0.4,
    app: "other",
  });
  if (!raw?.trim()) throw new Error("The AI returned an empty response. Try again.");

  let parsedJson: unknown;
  try {
    parsedJson = JSON.parse(extractJson(raw));
  } catch {
    throw new Error("The AI response was not valid JSON. Try again.");
  }

  const result = DraftSchema.safeParse(parsedJson);
  if (!result.success) {
    throw new Error(`AI draft failed validation: ${result.error.issues[0]?.message ?? "bad shape"}`);
  }

  return {
    label: result.data.label,
    docsLibraryId: result.data.docsLibraryId ?? undefined,
    standards: result.data.standards,
    commonErrors: result.data.commonErrors,
    goldenLibraries: result.data.goldenLibraries,
  };
}
